import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { track } from "./lib/analytics";
import { Nav } from "./components/v2/Nav";
import { Hero } from "./components/v2/Hero";
import { ProofBar } from "./components/v2/ProofBar";
import { Beliefs } from "./components/v2/Beliefs";
import { Problem } from "./components/v2/Problem";
import { Pillars } from "./components/v2/Pillars";
import { EditorialMarquee } from "./components/v2/EditorialMarquee";
import { Showcase } from "./components/v2/Showcase";
import { People } from "./components/v2/People";
import { Ask } from "./components/v2/Ask";
import { HowItWorks } from "./components/v2/HowItWorks";
import { PrivacyBand } from "./components/v2/PrivacyBand";
import { Faq } from "./components/v2/Faq";
import { Transformation } from "./components/v2/Transformation";
import { FullBleed } from "./components/v2/FullBleed";
import { Testimonials } from "./components/v2/Testimonials";
import { ReadFragment } from "./components/v2/ReadFragment";
import { Comparison } from "./components/v2/Comparison";
import { SecureCode } from "./components/v2/secure/SecureCode";
import { FinalCta } from "./components/v2/FinalCta";
import { Footer } from "./components/v2/Footer";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Science from "./pages/Science";
import Claims from "./pages/Claims";

const Landing = () => {
  const ctaRef = useRef(null);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    let t;
    if (window.location.hash) {
      t = setTimeout(() => {
        const el = document.querySelector(window.location.hash);
        if (el) lenis.scrollTo(el, { offset: -56, duration: 1.4 });
      }, 600);
    }
    return () => {
      clearTimeout(t);
      lenis.destroy();
    };
  }, []);

  const scrollToCta = () => {
    track("cta_create_source_code", { location: "primary" });
    if (lenisRef.current && ctaRef.current) {
      lenisRef.current.scrollTo(ctaRef.current, { offset: -56, duration: 1.4 });
    } else {
      ctaRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="grain bg-[#0B0B0C] text-[#F2F0EB]">
      <Nav onCta={scrollToCta} />
      <Hero onCta={scrollToCta} />
      <Problem />
      <ProofBar />
      <SecureCode ref={ctaRef} />
      <FullBleed />
      <Pillars />
      <Showcase />
      <People />
      <Ask />
      <EditorialMarquee />
      <HowItWorks />
      <Transformation />
      {/* Testimonials hidden until we have real user reviews */}
      <ReadFragment />
      <Comparison />
      <Beliefs />
      <Faq />
      <PrivacyBand />
      <FinalCta />
      <Footer />
    </div>
  );
};


function CanonicalManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    let link = document.getElementById("canonical-link");
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      link.id = "canonical-link";
      document.head.appendChild(link);
    }
    link.href =
      "https://sourcecoded.io" +
      (pathname === "/" ? "/" : pathname.replace(/\/+$/, ""));
  }, [pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <CanonicalManager />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/science" element={<Science />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/claims" element={<Claims />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="bottom-center" theme="dark" />
    </BrowserRouter>
  );
}

export default App;
